import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';import { useNavigate } from 'react-router-dom';

import {
  Zap, Plus, Layout, Clock, Settings, ArrowRight,
  CheckCircle2, Folder, Terminal, AlertTriangle,
  Loader2, ChevronLeft, LogOut
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { supabase } from './supabaseClient';

// --- CONFIG ---
const CORE_API = "https://everydaytok-thrust-core-server.hf.space"; // process.env.REACT_APP_CORE_SERVER_URL1;

// --- UTILS ---
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- TYPES ---
type ViewState = 'dashboard' | 'create' | 'workspace';
type TabState = 'momentum' | 'timeline' | 'settings';

interface Project {
  id: string;
  name: string;
  description: string;
  status: string;
  updated_at: string;
  requirements_doc: string;
}

interface Thrust {
  id: string;
  title: string;
  markdown_content: string;
  tasks: { id: string; title: string; status: string }[];
}

interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  type: string;
  created_at: string;
}

// ==========================================
// COMPONENT: DASHBOARD
// ==========================================
export default function Dashboard() {
  const [view, setView] = useState('dashboard');
  const [activeProject, setActiveProject] = useState(null);
	const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auth State
  const [user, setUser] = useState(null);
	const [userId, setUserId] = useState(null);
  const [accessToken, setAccessToken] = useState(null);



	 // --- AUTH LISTENER ---                              
  useEffect(() => {                                    

    // 2. Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setAccessToken(session.access_token);
        setUserId(session.user.id);                          } else {
       // navigate('/auth');
      }
    });
                                                           return () => subscription.unsubscribe();             }, [navigate]);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      setUser(session.user);
	
        setAccessToken(session.access_token);                  setUserId(session.user.id);                          
      
      // 1. Fetch the list for the background/dashboard
      fetchProjects(session.user.id);

      // 2. Check URL for specific navigation instructions
      await handleUrlNavigation();
    } else {
      console.log("No session");
      setLoading(false);
    }
  };

  // --- URL QUERY HANDLER ---
  const handleUrlNavigation = async () => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('projectId');
    const viewParam = params.get('view');

    // Scenario 1: Open specific project
    if (projectId) {
      setLoading(true); // Keep loading true while we fetch the specific lead
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', projectId)
        .single();

      if (!error && data) {
        setActiveProject(data);
        setView('workspace');
      }
      setLoading(false);
    } 
    // Scenario 2: Go to Create Page
    else if (viewParam === 'create') {
      setView('create');
      setLoading(false);
    }
    // Default: Dashboard
    else {
      setView('dashboard');
      setLoading(false);
    }
  };

  const fetchProjects = async (userId: string) => {
    // We don't want to set global loading to true here if we are already
    // processing a deep link, so we just fetch the data.
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (!error && data) setProjects(data);
    
    // Only turn off loading if we aren't waiting for a specific project via URL
    if (!window.location.search.includes('projectId')) {
       setLoading(false);
    }
  };

  const handleOpenProject = (project: Project) => {
    setActiveProject(project);
    setView('workspace');
    // Optional: Update URL without refresh for bookmarking
    const url = new URL(window.location.href);
    url.searchParams.set('projectId', project.id);
    window.history.pushState({}, '', url);
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setActiveProject(null);
    // Clear URL params
    const url = new URL(window.location.href);
    url.searchParams.delete('projectId');
    url.searchParams.delete('view');
    window.history.pushState({}, '', url);
  };

  // --- RENDER ---
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 font-sans selection:bg-purple-500/30">
      {/* NAV BAR */}
      <nav className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-md sticky top-0 z-50 h-16">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div
            className="flex items-center gap-2 font-black text-xl tracking-tighter italic cursor-pointer"
            onClick={handleBackToDashboard}
          >
            <Zap className="text-purple-500 fill-current" size={20} />
            THRUST
          </div>

          <div className="flex items-center gap-4">
            {activeProject && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-xs font-bold text-zinc-400">
                <Folder size={12} />
                {activeProject.name}
              </div>
            )}
            <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 ring-2 ring-black" />
          </div>
        </div>
      </nav>

      {/* MAIN CONTENT AREA */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">

          {view === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-8"
            >
              <div className="flex justify-between items-end">
                <div>
                  <h1 className="text-3xl font-black tracking-tight mb-2">Your Projects</h1>
                  <p className="text-zinc-500 font-medium">Select a Lead to enter flow state.</p>
                </div>
                <button
                  onClick={() => setView('create')}
                  className="bg-zinc-100 text-zinc-900 hover:bg-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors"
                >
                  <Plus size={16} /> New Lead
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="animate-spin text-zinc-700" /></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => handleOpenProject(p)}
                      className="group p-6 rounded-2xl border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900 hover:border-zinc-700 transition-all cursor-pointer relative overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 p-6 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowRight className="text-zinc-500 group-hover:text-purple-500 -rotate-45 group-hover:rotate-0 transition-transform duration-300" />
                      </div>
                      <div className="mb-4">
                        <div className="h-10 w-10 rounded-lg bg-zinc-800 flex items-center justify-center mb-4 group-hover:bg-purple-500/10 group-hover:text-purple-400 transition-colors text-zinc-500">
                          <Terminal size={20} />
                        </div>
                        <h3 className="font-bold text-lg mb-1">{p.name}</h3>
                        <p className="text-sm text-zinc-500 line-clamp-2">{p.description}</p>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {p.status}
                      </div>
                    </div>
                  ))}

                  {/* Empty State Create Card */}
                  <button
                    onClick={() => setView('create')}
                    className="p-6 rounded-2xl border border-dashed border-zinc-800 bg-transparent hover:bg-zinc-900/50 hover:border-zinc-700 transition-all flex flex-col items-center justify-center gap-4 text-zinc-600 hover:text-zinc-400 h-[220px]"
                  >
                    <Plus size={32} />
                    <span className="font-bold text-sm">Initialize New Project</span>
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {view === 'create' && (
            <CreateProjectView
              userId={user?.id}
	      accessToken={accessToken}
              onBack={handleBackToDashboard}
              onCreated={(newProject: Project) => {
                setProjects([newProject, ...projects]);
                handleOpenProject(newProject);
              }}
            />
          )}

          {view === 'workspace' && activeProject && (
            <WorkspaceView project={activeProject} onBack={handleBackToDashboard} />
          )}

        </AnimatePresence>
      </main>
    </div>
  );
}

// ==========================================
// SUB-COMPONENT: CREATE PROJECT VIEW
// ==========================================
function CreateProjectView({ onBack, onCreated, userId }: any, accessToken) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {

      // CALL CORE SERVER to Init AI Architect
      const response = await fetch(`${CORE_API}/init-project`, {
        method: 'POST',
        headers: {
	'Content-Type': 'application/json',
	'Authorization': `Bearer ${accessToken}`
	},
        body: JSON.stringify({
          userId: userId,
          name: name,
          description: desc,
          localPath: "" // Optional in web dashboard
        })
      });

      const result = await response.json();

      if (result.success) {
        // Fetch the newly created lead from Supabase to ensure full object
        const { data } = await supabase.from('leads').select('*').eq('id', result.leadId).single();
        if (data) onCreated(data);
      }
    } catch (error) {
      console.error("Init Failed", error);
      alert("Failed to initialize project. Is Core Server running?");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="max-w-2xl mx-auto"
    >
      <button onClick={onBack} className="mb-6 text-zinc-500 hover:text-white flex items-center gap-2 text-sm font-bold transition-colors">
        <ChevronLeft size={16} /> Back to Dashboard
      </button>

      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 shadow-2xl">
        <div className="mb-8">
          <h2 className="text-2xl font-black mb-2">Initialize New Lead</h2>
          <p className="text-zinc-500 text-sm">The AI Architect will analyze your request and generate a PRD and Morning Briefing.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Project Name</label>
            <input
              required
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500 transition-colors font-bold"
              placeholder="e.g. E-Commerce Refactor"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Description / Aim</label>
            <textarea
              required
              value={desc}
              onChange={e => setDesc(e.target.value)}
              className="w-full h-32 bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500 transition-colors resize-none leading-relaxed"
              placeholder="Describe what you want to build. The AI will use this to generate the Requirements Document."
            />
          </div>
          <div className="pt-4">
            <button
              disabled={isSubmitting}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isSubmitting ? <Loader2 className="animate-spin" /> : <Zap size={18} fill="currentColor" />}
              {isSubmitting ? "Architecting..." : "Initialize Thrust"}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}

// ==========================================
// SUB-COMPONENT: WORKSPACE VIEW
// ==========================================
function WorkspaceView({ project, onBack }: { project: Project, onBack: () => void }) {
  const [tab, setTab] = useState('momentum');
  const [thrust, setThrust] = useState(null);
  const [timeline, setTimeline] = useState([]);
	const navigate = useNavigate();

  useEffect(() => {
    fetchWorkspaceData();
  }, [project.id]);

  const fetchWorkspaceData = async () => {
    // 1. Fetch Latest Active Thrust
    const { data: thrusts } = await supabase
      .from('thrusts')
      .select('*, tasks:thrust_tasks(*)')
      .eq('lead_id', project.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (thrusts) setThrust(thrusts);

    // 2. Fetch Timeline
    const { data: events } = await supabase
      .from('timeline_events')
      .select('*')
      .eq('lead_id', project.id)
      .order('created_at', { ascending: false });

    if (events) setTimeline(events);
  };

  const toggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'done' ? 'pending' : 'done';

    // Optimistic UI Update
    if (thrust) {
      const updatedTasks = thrust.tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t);
      setThrust({ ...thrust, tasks: updatedTasks });
    }

    await supabase.from('thrust_tasks').update({ status: newStatus }).eq('id', taskId);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full"
    >
      {/* Header / Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 border-b border-zinc-800 pb-6">
        <div>
          <button onClick={onBack} className="text-zinc-500 text-xs font-bold uppercase tracking-widest hover:text-white mb-2 flex items-center gap-1">
            <ChevronLeft size={12} /> Dashboard
          </button>
          <h1 className="text-3xl font-black">{project.name}</h1>
        </div>

        <div className="flex bg-zinc-900 p-1 rounded-xl border border-zinc-800">
          <TabButton active={tab === 'momentum'} onClick={() => setTab('momentum')} icon={Zap} label="Briefing" />
          <TabButton active={tab === 'timeline'} onClick={() => setTab('timeline')} icon={Clock} label="Timeline" />
          <TabButton active={tab === 'settings'} onClick={() => setTab('settings')} icon={Settings} label="Settings" />
        </div>
      </div>

      {/* Content Area */}
      <div className="min-h-[500px]">
        {tab === 'momentum' && (
          <div className="grid lg:grid-cols-[1fr_350px] gap-8">
            {/* Markdown Content */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6 border-b border-zinc-800 pb-4">
                <div className="p-2 bg-purple-500/10 text-purple-400 rounded-lg">
                  <Layout size={20} />
                </div>
                <span className="font-bold text-sm text-zinc-400">Current Thrust Directive</span>
              </div>
              <div className="prose prose-invert prose-purple max-w-none">
                {thrust ? (
                  <ReactMarkdown>{thrust.markdown_content}</ReactMarkdown>
                ) : (
                  <div className="text-center py-20 text-zinc-500">
                    <Loader2 className="animate-spin mx-auto mb-4" />
                    <p>Initializing Project Architecture...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <h3 className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-4">Mission Checklist</h3>
                <div className="space-y-3">
                  {thrust?.tasks?.map(task => (
                    <div
                      key={task.id}
                      onClick={() => toggleTask(task.id, task.status)}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-zinc-800 cursor-pointer transition-colors group"
                    >
                      <div className={cn(
                        "mt-0.5 h-5 w-5 rounded border flex items-center justify-center transition-colors",
                        task.status === 'done' ? "bg-purple-600 border-purple-600" : "border-zinc-600 group-hover:border-zinc-400"
                      )}>
                        {task.status === 'done' && <CheckCircle2 size={14} className="text-white" />}
                      </div>
                      <span className={cn("text-sm font-medium leading-tight", task.status === 'done' ? "text-zinc-500 line-through" : "text-zinc-200")}>
                        {task.title}
                      </span>
                    </div>
                  ))}
                  {(!thrust?.tasks || thrust.tasks.length === 0) && (
                    <p className="text-xs text-zinc-600 italic">No active tasks.</p>
                  )}
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <h3 className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-4">Project Context</h3>
                <div className="space-y-4">
                  <ContextItem label="PRD Status" value="Locked" color="text-emerald-500" />
                  <ContextItem label="Core API" value="Connected" color="text-emerald-500" />
                  <ContextItem label="Last Sync" value="Just now" color="text-zinc-400" />
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'timeline' && (
          <div className="max-w-3xl mx-auto">
            <div className="space-y-8 relative before:absolute before:left-4 before:top-0 before:bottom-0 before:w-0.5 before:bg-zinc-800">
              {timeline.map((event) => (
                <div key={event.id} className="relative pl-12">
                  <div className={cn(
                    "absolute left-2 top-1.5 h-4 w-4 rounded-full border-2 border-zinc-950 -translate-x-1/2",
                    event.type === 'feature' ? "bg-purple-500" :
                      event.type === 'error' ? "bg-red-500" : "bg-zinc-600"
                  )} />

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <span className={cn(
                          "text-[10px] font-bold px-2 py-0.5 rounded uppercase",
                          event.type === 'feature' ? "bg-purple-500/10 text-purple-400" : "bg-zinc-800 text-zinc-400"
                        )}>
                          {event.type}
                        </span>
                        <span className="text-xs text-zinc-500 font-mono">
                          {new Date(event.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <h4 className="font-bold text-lg text-white mb-2">{event.title}</h4>
                    <p className="text-zinc-400 text-sm leading-relaxed">{event.description}</p>
                  </div>
                </div>
              ))}
              {timeline.length === 0 && (
                <div className="text-center py-20 text-zinc-500 pl-12">
                  No timeline events recorded yet.
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'settings' && (
          <div className="max-w-2xl">
            <h3 className="text-xl font-bold mb-6">Settings</h3>
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-6">
              <h4 className="font-bold mb-4">Danger Zone</h4>
              <p className="text-sm text-zinc-500 mb-6">Once you archive a project, the AI context is frozen.</p>
              <button className="border border-red-500/20 bg-red-500/5 text-red-500 px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-500/10 transition-colors">
                Archive Project
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// --- SMALL COMPONENTS ---
const TabButton = ({ active, onClick, icon: Icon, label }: any) => (
  <button
    onClick={onClick}
    className={cn(
      "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all",
      active ? "bg-zinc-800 text-white shadow-sm" : "text-zinc-500 hover:text-zinc-300"
    )}
  >
    <Icon size={16} />
    {label}
  </button>
);

const ContextItem = ({ label, value, color }: any) => (
  <div className="flex justify-between items-center text-sm">
    <span className="text-zinc-500 font-medium">{label}</span>
    <span className={cn("font-bold", color)}>{value}</span>
  </div>
);
